import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const BabyNameNumerologyCalculator = () => {
  const [name, setName] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  // Helper function to convert letters to numbers
  const letterToNumber = (letter) => {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    return letters.indexOf(letter) + 1;
  };

  // Helper function to reduce numbers to single digit
  const reduceNumber = (num) => {
    while (num > 9 && ![11, 22, 33].includes(num)) {
      num = num.toString().split('').reduce((sum, digit) => sum + +digit, 0);
    }
    return num;
  };

  // Helper function to calculate Baby Name Numerology
  const calculateBabyNameNumerology = (inputName) => {
    const name = inputName.toUpperCase();
    const letters = name.split('');
    const babyNumbers = [];
    
    // Calculate numbers for each letter
    for (let i = 0; i < letters.length; i++) {
      if (letters[i] !== ' ') {
        const num = letterToNumber(letters[i]);
        const reduced = reduceNumber(num);
        babyNumbers.push({
          letter: letters[i],
          number: reduced
        });
      }
    }

    // Calculate total baby number
    const totalBabyNumber = babyNumbers.reduce((sum, item) => sum + item.number, 0);
    const totalReduced = reduceNumber(totalBabyNumber);

    return {
      babyNumbers,
      totalBabyNumber,
      totalReduced
    };
  };

  const handleInputChange = (e) => {
    setName(e.target.value);
    setError('');
  };

  const calculateBabyName = () => {
    if (!name.trim()) {
      setError('Please enter a baby name');
      return;
    }

    const babyData = calculateBabyNameNumerology(name);
    setResults(babyData);

    // Prepare chart data
    const babyChartData = {
      labels: babyData.babyNumbers.map(item => item.letter),
      datasets: [{
        label: 'Baby Name Numerology',
        data: babyData.babyNumbers.map(item => item.number),
        backgroundColor: [
          'rgba(255, 159, 64, 0.7)',
          'rgba(75, 192, 192, 0.7)',
          'rgba(255, 99, 132, 0.7)',
          'rgba(54, 162, 235, 0.7)',
          'rgba(153, 102, 255, 0.7)'
        ],
        borderColor: [
          'rgba(255, 159, 64, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(153, 102, 255, 1)'
        ],
        borderWidth: 1,
        hoverOffset: 4
      }]
    };

    setChartData(babyChartData);
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Baby Name Numerology Calculator | Calculate Your Baby's Life Path</title>
        <meta name="description" content="Calculate your baby's name numerology using our Numerology Calculator. Understand the personality traits and life path of your baby's name." />
        <meta name="keywords" content="baby name numerology, numerology calculator, baby name analysis, personality traits, life path" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://yourwebsite.com/calculator/baby-name" />
        <meta property="og:title" content="Baby Name Numerology Calculator | Calculate Your Baby's Life Path" />
        <meta property="og:description" content="Calculate your baby's name numerology using our Numerology Calculator. Understand the personality traits and life path of your baby's name." />
        <meta property="og:image" content="https://yourwebsite.com/images/baby-numerology-calculator.png" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://yourwebsite.com/calculator/baby-name" />
        <meta property="twitter:title" content="Baby Name Numerology Calculator | Calculate Your Baby's Life Path" />
        <meta property="twitter:description" content="Calculate your baby's name numerology using our Numerology Calculator. Understand the personality traits and life path of your baby's name." />
        <meta property="twitter:image" content="https://yourwebsite.com/images/baby-numerology-calculator.png" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">👶</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Baby Name Numerology Calculator</h1>
            <p className="text-lg text-purple-600 font-semibold">Calculate Your Baby's Life Path</p>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
            Baby's Name:
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={name}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter your baby's name"
          />
        </div>

        <button
          onClick={calculateBabyName}
          className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Calculate Baby Name Numerology
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {results && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Your Baby's Name Numerology</h3>
            
            <div className="space-y-12">
              {/* Baby Analysis Section */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h4 className="text-xl font-semibold mb-6">Baby Analysis</h4>
                <div className="space-y-8">
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Total Baby Number:</span>
                      <span className="font-medium text-purple-600 text-2xl">{results.totalReduced}</span>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                    <h5 className="font-medium mb-4">Letter Analysis:</h5>
                    <div className="space-y-2">
                      {results.babyNumbers.map((item, index) => (
                        <div key={index} className="flex justify-between items-center">
                          <span className="text-gray-600">{item.letter}:</span>
                          <span className="font-medium">{item.number}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Baby Visualization Section */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h4 className="text-xl font-semibold mb-6">Baby Visualization</h4>
                <div className="h-96 bg-gray-50 rounded-xl p-6">
                  <Doughnut
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        title: {
                          display: true,
                          text: 'Baby Name Numerology by Letter',
                          color: '#4F46E5',
                          font: {
                            size: 16
                          }
                        },
                        legend: {
                          display: true,
                          position: 'bottom',
                          labels: {
                            color: '#4B5563',
                            font: {
                              size: 12
                            }
                          }
                        }
                      },
                      cutout: '60%',
                      animation: {
                        animateRotate: true,
                        animateScale: true
                      }
                    }}
                  />
                </div>
              </div>

              {/* Step-by-Step Explanation Section */}
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h4 className="text-xl font-semibold mb-6">Step-by-Step Explanation</h4>
                <div className="space-y-6">
                  <div>
                    <h5 className="font-medium text-purple-600 mb-3">1. Letter Conversion</h5>
                    <p className="text-gray-700">
                      Each letter in your baby's name is converted to a number using the following system:
                      <br />
                      A=1, B=2, C=3, D=4, E=5, F=6, G=7, H=8, I=9, J=1, K=2, L=3, M=4, N=5, O=6, P=7, Q=8, R=9, S=1, T=2, U=3, V=4, W=5, X=6, Y=7, Z=8
                    </p>
                  </div>

                  <div>
                    <h5 className="font-medium text-purple-600 mb-3">2. Number Reduction</h5>
                    <p className="text-gray-700">
                      The numbers are reduced to single digits by adding their individual digits together until a single digit is obtained. For example:
                      <br />
                      17 → 1 + 7 = 8
                      <br />
                      23 → 2 + 3 = 5
                    </p>
                  </div>

                  <div>
                    <h5 className="font-medium text-purple-600 mb-3">3. Total Baby Number</h5>
                    <p className="text-gray-700">
                      All the reduced numbers are added together to get the total baby number, which is then reduced to a single digit. This final number represents the overall numerological vibration of your baby's name.
                    </p>
                  </div>

                  <div>
                    <h5 className="font-medium text-purple-600 mb-3">4. Baby Number Meanings</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h6 className="text-lg font-semibold mb-2">1 - Leadership</h6>
                        <ul className="list-disc pl-6 space-y-1">
                          <li>Strong leadership potential</li>
                          <li>Independent spirit</li>
                          <li>Confidence and courage</li>
                          <li>Self-motivated</li>
                        </ul>
                      </div>
                      <div>
                        <h6 className="text-lg font-semibold mb-2">2 - Partnership</h6>
                        <ul className="list-disc pl-6 space-y-1">
                          <li>Peaceful nature</li>
                          <li>Cooperative spirit</li>
                          <li>Harmonious relationships</li>
                          <li>Empathetic</li>
                        </ul>
                      </div>
                      <div>
                        <h6 className="text-lg font-semibold mb-2">3 - Creativity</h6>
                        <ul className="list-disc pl-6 space-y-1">
                          <li>Artistic talent</li>
                          <li>Imaginative thinking</li>
                          <li>Expressive personality</li>
                          <li>Optimistic outlook</li>
                        </ul>
                      </div>
                      <div>
                        <h6 className="text-lg font-semibold mb-2">4 - Stability</h6>
                        <ul className="list-disc pl-6 space-y-1">
                          <li>Practical approach</li>
                          <li>Responsible behavior</li>
                          <li>Organized personality</li>
                          <li>Reliable nature</li>
                        </ul>
                      </div>
                      <div>
                        <h6 className="text-lg font-semibold mb-2">5 - Change</h6>
                        <ul className="list-disc pl-6 space-y-1">
                          <li>Adventurous spirit</li>
                          <li>Flexible personality</li>
                          <li>Quick learner</li>
                          <li>Curious nature</li>
                        </ul>
                      </div>
                      <div>
                        <h6 className="text-lg font-semibold mb-2">6 - Harmony</h6>
                        <ul className="list-disc pl-6 space-y-1">
                          <li>Family-oriented</li>
                          <li>Compassionate nature</li>
                          <li>Protective instincts</li>
                          <li>Service-oriented</li>
                        </ul>
                      </div>
                      <div>
                        <h6 className="text-lg font-semibold mb-2">7 - Wisdom</h6>
                        <ul className="list-disc pl-6 space-y-1">
                          <li>Deep thinker</li>
                          <li>Intuitive understanding</li>
                          <li>Seeker of knowledge</li>
                          <li>Spiritual connection</li>
                        </ul>
                      </div>
                      <div>
                        <h6 className="text-lg font-semibold mb-2">8 - Success</h6>
                        <ul className="list-disc pl-6 space-y-1">
                          <li>Ambitious nature</li>
                          <li>Material success</li>
                          <li>Leadership qualities</li>
                          <li>Business acumen</li>
                        </ul>
                      </div>
                      <div>
                        <h6 className="text-lg font-semibold mb-2">9 - Completion</h6>
                        <ul className="list-disc pl-6 space-y-1">
                          <li>Humanitarian spirit</li>
                          <li>Global perspective</li>
                          <li>Philosophical mind</li>
                          <li>Compassionate leader</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Examples & Interpretations <span className="ml-2 text-2xl">📊</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Example 1: Creative Child</h3>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium mb-2">Baby Name: Emma</p>
                <p className="text-purple-600 text-2xl mb-2">Total Number: 3</p>
                <p className="mt-4 text-gray-700">
                  Interpretation: This name suggests a creative and expressive child with artistic talents and an optimistic outlook.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Example 2: Strong Leader</h3>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium mb-2">Baby Name: Alexander</p>
                <p className="text-purple-600 text-2xl mb-2">Total Number: 1</p>
                <p className="mt-4 text-gray-700">
                  Interpretation: This name indicates a natural leader with strong independent spirit and confidence.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Baby Number Meanings</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-lg font-semibold mb-2">1 - Natural Leader</h4>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Independent spirit</li>
                    <li>Confident personality</li>
                    <li>Strong leadership</li>
                    <li>Self-motivated</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-lg font-semibold mb-2">3 - Creative Expression</h4>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Artistic talent</li>
                    <li>Imaginative thinking</li>
                    <li>Expressive personality</li>
                    <li>Optimistic outlook</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to calculate your baby's name numerology:
          </p>
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 1: Enter Baby's Name</h3>
              <p className="text-gray-700">
                Input your baby's name in the input field.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 2: Calculate</h3>
              <p className="text-gray-700">
                Click the "Calculate Baby Name Numerology" button to see your results.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 3: Understand Results</h3>
              <p className="text-gray-700">
                Review your baby's total number and letter analysis to understand their potential personality traits and life path.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          FAQ <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-2">What is Baby Name Numerology?</h3>
              <p className="text-gray-700">
                Baby Name Numerology analyzes the numbers associated with your baby's name to reveal their potential personality traits, strengths, and life path.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">How is it calculated?</h3>
              <p className="text-gray-700">
                Each letter in your baby's name is assigned a number (A=1, B=2, etc.), and these numbers are reduced to single digits to reveal their numerological potential.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">What does it reveal?</h3>
              <p className="text-gray-700">
                It shows potential personality traits, strengths, challenges, and life path opportunities for your baby.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Key Features</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Accurate baby name numerology calculation</li>
                <li>Letter analysis for baby names</li>
                <li>Visual representation</li>
                <li>Detailed personality interpretation</li>
                <li>Personalized insights</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Benefits</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Understand potential personality traits</li>
                <li>Make informed naming decisions</li>
                <li>Prepare for parenting</li>
                <li>Enhance name selection</li>
                <li>Support child development</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BabyNameNumerologyCalculator;
